import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { TableSortArgs } from '../../components/TableV2';
import { PAGE_PROPS } from '../constants/common.constants';
import { IPD_ADMISSION_INITIAL_STATE } from '../constants/ipdAdmissionSlice.constants';
import { IIPDAdmissionSavePayload } from '../types/ipdAdmissionSlice.types';

export const getAllIPDAdmissions = createAsyncThunk(
    'ipdAdmission/get',
    async (data: TableSortArgs) => {
        const response = await axios.post('/IPDAdmission/Search', data);
        return response.data;
    },
);

export const getIPDAdmissionById = createAsyncThunk(
    'ipdAdmission/getByID',
    async (id: number) => {
        const response = await axios.get(
            `/IPDAdmission/GetByAdmissionId/${id}`,
        );
        return response.data;
    },
);

export const saveIPDAdmission = createAsyncThunk(
    'ipdAdmission/save',
    async (data: IIPDAdmissionSavePayload) => {
        const response = await axios.post('/IPDAdmission/Save', data, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    },
);

export const updateIPDAdmission = createAsyncThunk(
    'ipdAdmission/update',
    async (data: IIPDAdmissionSavePayload & { id: number }) => {
        const response = await axios.post('/IPDAdmission/Update', data, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    },
);

export interface IShiftBedPayload {
    id: number;
    buildingId: number;
    wingId: number;
    floorId: number;
    wardId: number;
    roomId: number;
    bedId: number;
    fromDate: string;
    toDate: string;
    isCurrent: boolean;
}

export const shiftBed = createAsyncThunk(
    'ipdAdmission/shiftBed',
    async (
        {
            admissionId,
            payload,
        }: {
            admissionId: number;
            payload: IShiftBedPayload;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(
                `/IPDAdmission/ShiftBed/${admissionId}`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );
            return response.data;
        } catch (err: unknown) {
            const errObj = err as { response?: { data?: unknown }; message?: string };
            const data = errObj.response?.data;
            return rejectWithValue(data ?? { responseMessage: errObj.message });
        }
    },
);

export const getAdmissionBedHistory = createAsyncThunk(
    'ipdAdmission/getAdmissionBedHistory',
    async (admissionId: number) => {
        const response = await axios.get(
            `/IPDAdmission/GetAdmissionBedHistory/${admissionId}`,
        );
        return response.data;
    },
);

export const ipdAdmissionSlice = createSlice({
    name: 'ipdAdmission',
    initialState: IPD_ADMISSION_INITIAL_STATE,
    reducers: {
        onSelectIPDAdmission(state, action) {
            state.selectedIPDAdmission = action.payload;
        },
        onIPDAdmissionPageChange: (state, action) => {
            state.pageProps = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAllIPDAdmissions.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getAllIPDAdmissions.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.data = action.payload?.result || PAGE_PROPS;
                state.error = null;
            })
            .addCase(getAllIPDAdmissions.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(getIPDAdmissionById.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(getIPDAdmissionById.fulfilled, (state, action) => {
                state.loading = 'succeeded';
                state.selectedIPDAdmission = action.payload?.result;
                state.error = null;
            })
            .addCase(getIPDAdmissionById.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(saveIPDAdmission.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(saveIPDAdmission.fulfilled, (state) => {
                state.loading = 'succeeded';
                state.error = null;
            })
            .addCase(saveIPDAdmission.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(updateIPDAdmission.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(updateIPDAdmission.fulfilled, (state) => {
                state.loading = 'succeeded';
                state.error = null;
            })
            .addCase(updateIPDAdmission.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            })
            .addCase(shiftBed.pending, (state) => {
                state.loading = 'pending';
            })
            .addCase(shiftBed.fulfilled, (state) => {
                state.loading = 'succeeded';
                state.error = null;
            })
            .addCase(shiftBed.rejected, (state, action) => {
                state.loading = 'failed';
                state.error = action.error.message ?? 'An error occurred.';
            });
    },
});

export const { onSelectIPDAdmission, onIPDAdmissionPageChange } =
    ipdAdmissionSlice.actions;

export default ipdAdmissionSlice.reducer;
